import { useCallback, useMemo, useState } from 'react'
import { Outlet, To } from 'react-router-dom'

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { DocumentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { asDocuments } from 'modules/documents/components/dataCollectionAdapters'
import DraggableDatatable from 'modules/documents/components/DraggableDatatable'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACE, SPACES } from 'modules/documents/lib/constants'

import type { DocumentNode } from './types'
import { useFilters } from './useFilters'
import { useColumns } from './useColumns'
import { useBulkActions } from './useBulkActions'
import { useItemActions } from './useItemActions'

const ITEMS_PER_PAGE = 100

type Props = {
  onDrop: (files: File[]) => void
  folder?: Folder | null
  initialFolder?: Folder | null
  parentRoute?: To
  multiple?: boolean
  readonly: boolean
  space: SPACE
  onUpload?: () => void
}

const DataCollection = ({
  folder = null,
  initialFolder,
  parentRoute,
  onDrop,
  multiple = true,
  readonly,
  space,
}: Props) => {
  const [, setIsDragging] = useState(false)
  const { navigation } = useNavigation()
  const { filters, loading: filtersLoading } = useFilters()
  const { sortings, defaultSortings, columns } = useColumns(space)
  const { bulkActions, handleBulkAction, Modals } = useBulkActions({ space })
  const { itemActions, ItemActionModals } = useItemActions({ space })

  const isPublicSpace = space === SPACES.COMPANY_PUBLIC

  const source = useGraphqlDataCollectionSource({
    document: DocumentsDataCollectionDocument,
    paginationType: 'pages',
    perPage: ITEMS_PER_PAGE,
    includePaginationVars: false,

    mapData: (data) => {
      const edges = data?.documents?.documentsConnection?.edges ?? []
      return edges.map((edge) => edge.node)
    },

    mapVariables: ({ filters: filterState, sortings: sortingsState, search, pagination }) => {
      const filter: Record<string, unknown> = {}

      if (filterState?.createdAt) {
        const raw = filterState.createdAt
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const from = 'from' in raw && raw.from instanceof Date ? raw.from : undefined
          const to = 'to' in raw && raw.to instanceof Date ? raw.to : undefined
          if (from && to) {
            filter.createdAt = {
              between: [from.toISOString(), to.toISOString()],
            }
          }
        }
      }

      if (filterState?.visible) {
        const visibleValues = Array.isArray(filterState.visible)
          ? filterState.visible.filter((v): v is string => typeof v === 'string')
          : []
        if (visibleValues.length === 1) {
          filter.published = { eq: visibleValues[0] === 'true' }
        }
      }

      // Folder context: if a folder is selected, filter by it
      if (folder?.id) {
        filter.folder = { eq: folder.id }
      }

      return {
        space,
        search: search || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        sortOrder: sortingsState?.[0],
        first: pagination?.perPage ?? ITEMS_PER_PAGE,
        offsetPage: pagination?.currentPage ?? 1,
      }
    },

    mapPagination: (data) => {
      const connection = data?.documents?.documentsConnection
      return {
        total: connection?.totalCount ?? 0,
      }
    },

    search: {
      enabled: true,
    },
    filters,
    sortings,
    defaultSortings,
    selectable: readonly ? undefined : (item: DocumentNode) => item.id,
    bulkActions: readonly ? undefined : bulkActions,
    itemActions,
    itemUrl: useCallback(
      (item: DocumentNode) => {
        if (folder?.id) {
          // Inside a folder
          if (isPublicSpace) {
            return navigation.files.companyFiles.folderId.documentId.fullPath({
              document_id: item.id,
              folderId: folder.id,
            })
          }
          return navigation.files.managementDocuments.folderId.documentId.fullPath({
            document_id: item.id,
            folderId: folder.id,
          })
        }

        // Root level
        if (isPublicSpace) {
          return navigation.files.companyFiles.list.documentId.fullPath({
            document_id: item.id,
          })
        }
        return navigation.files.managementDocuments.list.documentId.fullPath({
          document_id: item.id,
        })
      },
      [navigation, folder?.id, isPublicSpace]
    ),
  })

  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records
  const documentsFromCollection = useMemo(() => asDocuments(records), [records])

  const outletContext = useMemo(
    () => ({
      documentsEntity: documentsFromCollection,
      folder,
      initialFolder,
      parentRoute,
      space,
      isLoading: false,
    }),
    [documentsFromCollection, folder, initialFolder, parentRoute, space]
  )

  if (filtersLoading) {
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )
  }

  const content = (
    <>
      <Modals />
      <ItemActionModals />
      <Box flexDirection="column" height="full">
        <Box flexGrow paddingX="s12" paddingTop="s12">
          <OneDataCollection
            fullHeight
            emptyStates={{
              'no-data': {
                title: i18n.t('documents.documents_datatable.empty_state.blank_state.title'),
                description: '',
              },
              'no-results': {
                title: i18n.t('documents.documents_datatable.empty_state.with_filters.title'),
                description: i18n.t(
                  'documents.documents_datatable.empty_state.with_filters.description'
                ),
              },
            }}
            source={source}
            onBulkAction={readonly ? undefined : handleBulkAction}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
      </Box>
      <Outlet context={outletContext} />
    </>
  )

  if (readonly) {
    return content
  }

  return (
    <DraggableDatatable onDrop={onDrop} multiple={multiple} onDragging={(v) => setIsDragging(v)}>
      {content}
    </DraggableDatatable>
  )
}

export default DataCollection
